/**
 * The firmware manifest, in ESP Web Tools' format.
 *
 * Served from this app's own hosting at a fixed path, so the version a device
 * is flashed with is the version this deployment carries — there is no
 * separate firmware server to fall out of step with. `scripts/publish-firmware.mjs`
 * writes it from `device/esp32/build`.
 */
export const FIRMWARE_MANIFEST_URL = "/firmware/manifest.json";

export interface FirmwarePart {
  path: string;
  offset: number;
}

export interface FirmwareBuild {
  chipFamily: string;
  parts: FirmwarePart[];
}

export interface FirmwareManifest {
  name: string;
  version: string;
  builds: FirmwareBuild[];
  /** Set by the publishing script while no real firmware has been copied in. */
  note?: string;
}

/** Whether what came back is a manifest a device could be flashed from. */
export function isFirmwareManifest(value: unknown): value is FirmwareManifest {
  const manifest = value as Partial<FirmwareManifest> | null;
  if (!manifest || typeof manifest !== "object") return false;
  if (typeof manifest.name !== "string" || typeof manifest.version !== "string") return false;
  if (!Array.isArray(manifest.builds) || manifest.builds.length === 0) return false;
  return manifest.builds.every(
    (build) =>
      typeof build?.chipFamily === "string" &&
      Array.isArray(build.parts) &&
      build.parts.length > 0 &&
      build.parts.every(
        (part) => typeof part?.path === "string" && Number.isInteger(part?.offset)
      )
  );
}

export async function loadManifest(url = FIRMWARE_MANIFEST_URL): Promise<FirmwareManifest> {
  // Never from cache: a deploy that shipped new firmware must be what the next
  // device gets, not what this browser saw last week.
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) throw new Error(`firmware manifest: ${response.status}`);
  const body: unknown = await response.json();
  if (!isFirmwareManifest(body)) throw new Error("firmware manifest: not a manifest");
  return body;
}
